import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import qs from 'qs';
import _get from 'lodash/get';
import useSiteConfigSelector from 'components/useSiteConfigSelector';
import useIsAuthPath from 'components/Hooks/useIsAuthPath';
import { makeSelectAuth } from 'containers/Auth/selectors';
import { makeSelectClientDetails } from 'containers/Main/selectors';
import { hasClientAccessToFeature } from 'utils/clientSettings';
import { getLoginSource } from 'utils/sourceTrackingService';

export default function useEmailLoginReminder() {
  const location = useLocation();
  const history = useHistory();
  const auth = useSelector(makeSelectAuth());
  const clientDetails = useSelector(makeSelectClientDetails());
  const allowSignIn = clientDetails?.signIn === 'Allow';
  const [siteConfig] = useSiteConfigSelector(['Features']);
  const [open, setOpen] = useState();
  const [reminderWasShown, setReminderWasShown] = useState();
  const isAuthPage = useIsAuthPath();

  const isCTAModalEnabled =
    allowSignIn &&
    hasClientAccessToFeature({
      featuresSiteConfig: siteConfig,
      featureName: 'authenticationCallToActionModal',
      clientDetails,
    });

  const query = useMemo(() => {
    const search = qs.parse(location.search, { ignoreQueryPrefix: true });
    const hashQuery = location.hash.split('?')[1] || '';
    const hash = qs.parse(hashQuery);
    return { ...search, ...hash };
  }, [location.search, location.hash]);

  const shouldShowReminder = useMemo(
    () =>
      query.utm_source === 'email' ||
      (auth.isLoaded && getLoginSource()?.lastLoginSource === 'email'),
    [auth.isLoaded, query],
  );

  const isLoginReminderFeatureEnabled = useMemo(
    () =>
      hasClientAccessToFeature({
        featuresSiteConfig: siteConfig,
        featureName: 'loginReminder',
        clientDetails,
      }),
    [clientDetails, siteConfig],
  );

  useEffect(() => {
    if (!auth.isLoaded) return;
    if (
      auth.isEmpty &&
      allowSignIn &&
      isLoginReminderFeatureEnabled &&
      shouldShowReminder &&
      !isAuthPage &&
      !isCTAModalEnabled
    ) {
      setOpen(true);
      setReminderWasShown(true);
    }
  }, [
    auth,
    allowSignIn,
    isLoginReminderFeatureEnabled,
    shouldShowReminder,
    isAuthPage,
    isCTAModalEnabled,
  ]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleLogin = useCallback(() => {
    const redirect = encodeURIComponent(
      `${location.pathname}${location.search}`,
    );
    const hasCustomAuthenticationEnabled = _get(
      clientDetails,
      'customAuthentication.enabled',
    );
    const pathname = hasCustomAuthenticationEnabled ? '/auth' : '/login';
    history.push({ pathname, search: `?redirect=${redirect}` });
    setOpen(false);
  }, [clientDetails, history, location.pathname, location.search]);

  return { open, handleClose, handleLogin, reminderWasShown };
}
